import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LoginPrompt from '../../Auth/LoginPrompt';
import { claimCoupon } from '../../api/couponService';
import Footer from '../../components/layout/Footer';
import { useToast } from '../../components/Toast';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import useScrollReveal from '../../hooks/useScrollReveal';
import { SECRET_50_GROUP_PRICES, GROUP_DISCOUNT_TIERS, GROUP50_EVENT_SLUG, getGroupPromoSlug, isSecret50GroupPromo, isGroup50EventPromo, GROUP50_EVENT_ENABLED } from '../../utils/groupPricing';
import { GROUP50_EVENT_END_LABEL, buildGroup50LandingUrl, isGroup50EventActive } from '../../utils/group50Campaign';
import {
  PRO50_BASE_PRICES,
  PRO50_EVENT_END_LABEL,
  PRO50_PROMO_SLUG,
  buildPro50LandingUrl,
  getPro50DiscountPercent,
  getPro50MonthlyPrice,
  getPro50Price,
  isPro50Active,
} from '../../utils/pro50Campaign';
// 📊 GA4 이벤트 트래킹
import { trackViewPricing, trackBeginCheckout } from '../../utils/analytics';
import { trackGroup50Event, trackSchoolPurchaseEvent } from '../../utils/eventTracker';
// isInApp/openInExternalBrowser 직접 호출 제거: PricingPage는 정보 페이지이므로
// 가드는 결제 페이지(ProSubscription/BulkPurchase/BillingKeyRegistration)의
// AppPaymentBlocker가 일관되게 담당. 외부 브라우저 직접 호출은 Apple 3.1.1
// 정책 위반 가능성으로 제거함.
import './PricingPage.css';
import { PRO_MODELS_TEXT } from '../../constants/aiModels';
import { getApiBaseUrl } from '../../utils/apiConfig';
import PlanCompareTable from './PlanCompareTable';

const PricingPage = () => {
  const { currentUser } = useAuth();
  const { subscriptionStatus, fetchSubscriptionStatus } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [showGroupInquiry, setShowGroupInquiry] = useState(false);
  // 'group' = 단체 구매 문의, 'enterprise' = 학교 생기부 검토 도입 문의 (같은 API, source만 다름)
  const [inquiryMode, setInquiryMode] = useState('group');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const pageRef = useRef(null);

  // 스크롤 리빌 — AI30 랜딩과 같은 패턴 (IO + 스태거 + reduced-motion 즉시 표시)
  useScrollReveal(pageRef, '[data-pr-reveal]');

  // 📊 GA4: 가격 페이지 조회 추적
  useEffect(() => {
    trackViewPricing();
  }, []);

  // URL 해시로 스크롤 (#docs). 견적기 임베드 시절의 #quotation 유입도 통합 밴드로 보낸다.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const targetId = id === 'quotation' ? 'docs' : id;
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    teacherCount: '',
    message: ''
  });
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);
  // 단체 문의 모달이 열리면 담당자명 입력으로 포커스
  const inquiryNameRef = useRef(null);

  // 쿠폰 관련 상태
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  const isPro = subscriptionStatus?.isPro || false;
  const inquiryTitle = inquiryMode === 'enterprise' ? '학교 생기부 검토 도입 문의' : '단체 구매 문의';
  const promoInfo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('promo');
    return {
      // 단체 구매 버튼으로 전달하는 공개 슬러그 (secret_50 + group50 이벤트 모두 포함)
      slug: getGroupPromoSlug(raw),
      isSecret50: isSecret50GroupPromo(raw),
      // group50은 10명 이상 단체 구매 전용 — 개인 플랜 가격에는 적용하지 않는다
      isGroup50: GROUP50_EVENT_ENABLED && isGroup50EventActive() && isGroup50EventPromo(raw),
    };
  }, [location.search]);
  // 개인 플랜 카드 가격은 secret_50 promo에서만 바뀐다
  const hasSecret50Promo = promoInfo.isSecret50;
  const promoSlug = promoInfo.isSecret50 ? promoInfo.slug : '';
  // 단체 50% 이벤트는 URL promo가 없어도 기간 중이면 "학교 · 단체" 밴드에 항상 보인다.
  // (팝업을 닫은 뒤 사이드바 "요금제"로 들어온 사용자가 이벤트를 다시 찾는 자리)
  const isGroup50Active = isGroup50EventActive();
  // 10월 PRO 최대 50% 이벤트 — 개인·단체 모두 인원 제한 없이 자동 적용
  const pro50Active = isPro50Active();
  const showGroup50 = !pro50Active && (promoInfo.isGroup50 || isGroup50Active);
  // 견적서 흐름에도 이벤트 promo를 기본 보존해 이벤트 단가가 견적 PDF까지 이어지게 한다.
  const schoolPromoSlug = promoInfo.slug
    || (pro50Active ? PRO50_PROMO_SLUG : (isGroup50Active ? GROUP50_EVENT_SLUG : ''));
  const checkoutPromoSlug = promoSlug || (pro50Active ? PRO50_PROMO_SLUG : '');
  const oneMonthPrice = getPro50Price('1month') ?? 24000;

  const proFromMonthly = pro50Active ? getPro50MonthlyPrice('12month') : 16667;
  const schoolPriceLabel = '별도 문의';

  // 단체 구매 할인 티어 요약 (0% 티어 제외, 5명부터 오름차순)
  const groupTiers = GROUP_DISCOUNT_TIERS
    .filter((tier) => tier.discountRate > 0)
    .slice()
    .reverse();

  const faqs = [
    {
      q: '학교 예산으로 결제할 수 있나요?',
      a: '네, 국내 서비스라서 가능해요. 법인카드, 공용카드 모두 쓸 수 있고 세금계산서도 발급해드려요.',
    },
    {
      q: '쿠폰을 미리 구매해두고 나중에 사용할 수 있나요?',
      a: '네, 가능해요. 왓퀴즈 쿠폰은 사용기한이 없어요. 예산이 있을 때 먼저 구매해두고 필요할 때 등록하면 돼요. 학교 예산 집행 시기와 실제 사용 시기가 달라도 걱정 없이 이용할 수 있어요.',
    },
    {
      q: '자동 결제되나요?',
      a: '아니요. 선택한 기간이 끝나면 자동으로 FREE로 전환돼요. 추가 청구 없이 안심하고 이용할 수 있어요.',
    },
    {
      q: '에듀집 체크리스트 등록이 되어 있나요?',
      a: '네, 학습지원 소프트웨어 필수기준 체크리스트, 에듀테크 소프트랩 실증 우수기업, 개인정보 처리방침 등 모든 서류를 준비해두었어요. 위 "학교 예산으로 결제하세요"에서 한 번에 다운로드할 수 있어요. 추가로 필요한 서류가 있으면 문의해주세요.',
    },
    {
      q: '환불이 가능한가요?',
      a: '결제 후 7일 이내라면 사용 이력과 무관하게 100% 전액 자동 환불돼요 (마이페이지에서 즉시 처리). 7일이 지나면 자동 환불은 어렵고, 회사 귀책 등 특별한 사정이 있으면 별도 문의로 검토해요. 환불 처리는 영업일 기준 3-5일 이내에 완료돼요.',
    },
    {
      q: '해외 AI 서비스와 뭐가 다른가요?',
      a: '국내 결제라서 학교 예산을 쓸 수 있고, 세금계산서도 발급돼요. GPT, Claude, Gemini를 하나의 구독으로 모두 사용할 수 있어요.',
    },
  ];

  const plans = useMemo(() => {
    const basePlans = [
      { id: '3month', duration: '3개월', price: 60000, originalPrice: 72000, discount: 17, monthly: 20000, badge: null, ctaText: '3개월 시작하기' },
      { id: '6month', duration: '6개월', price: 110000, originalPrice: 144000, discount: 24, monthly: 18333, badge: '추천', ctaText: '6개월 PRO 시작' },
      { id: '12month', duration: '12개월', price: 200000, originalPrice: 288000, discount: 31, monthly: 16667, badge: null, ctaText: '연간 최저가로 시작' },
    ];
    const promoPlans = !hasSecret50Promo ? basePlans : basePlans.map((plan) => {
      const promoPrice = SECRET_50_GROUP_PRICES[plan.id] || plan.price;
      const discount = Math.round((1 - promoPrice / plan.price) * 100);
      return {
        ...plan,
        price: promoPrice,
        originalPrice: plan.price,
        discount,
        monthly: Math.round(promoPrice / Number(plan.id.replace('month', ''))),
        ctaText: '전용 혜택가로 시작',
      };
    });
    if (!pro50Active) return promoPlans;

    // 10월 이벤트가와 전용 혜택가 중 싼 쪽 하나 (서버와 같은 규칙, 중복 할인 없음)
    return promoPlans.map((plan) => {
      const eventPrice = getPro50Price(plan.id);
      if (eventPrice == null || eventPrice > plan.price) return plan;
      return {
        ...plan,
        price: eventPrice,
        originalPrice: PRO50_BASE_PRICES[plan.id],
        discount: getPro50DiscountPercent(plan.id),
        monthly: getPro50MonthlyPrice(plan.id),
        badge: plan.id === '12month' ? '10월 반값' : null,
        ctaText: '10월 할인가로 시작',
      };
    });
  }, [hasSecret50Promo, pro50Active]);

  // 하단/스티키 CTA 가격이 프로모·이벤트 중에도 카드 가격과 어긋나지 않도록 플랜에서 동적으로 계산
  const recommendedPlan = useMemo(
    () => plans.find((plan) => plan.id === (pro50Active ? '12month' : '6month')) || plans[1],
    [plans, pro50Active]
  );

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingInquiry) return;
    
    setIsSubmittingInquiry(true);
    
    try {
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/api/user/group-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...inquiryForm,
          ...(inquiryMode === 'enterprise' ? { source: 'enterprise' } : {}),
          ...(() => {
            try {
              const s = localStorage.getItem('whatquiz_utm');
              if (!s) return {};
              const p = JSON.parse(s);
              if (p.expiresAt && Date.now() > p.expiresAt) return {};
              return {
                utm_source: p.utm_source || null,
                utm_medium: p.utm_medium || null,
                utm_campaign: p.utm_campaign || null,
                utm_content: p.utm_content || null,
              };
            } catch { return {}; }
          })(),
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '문의를 접수하지 못했어요.');
      }

      if (typeof window.fbq !== 'undefined') {
        const _utm = (() => { try { const s = localStorage.getItem('whatquiz_utm'); if (!s) return {}; const p = JSON.parse(s); return (!p.expiresAt || Date.now() <= p.expiresAt) ? p : {}; } catch { return {}; } })();
        window.fbq('track', 'Lead', {
          content_name: inquiryMode === 'enterprise' ? '도입문의' : '단체문의',
          value: 0,
          currency: 'KRW',
          utm_source: _utm.utm_source || undefined,
          utm_medium: _utm.utm_medium || undefined,
          utm_campaign: _utm.utm_campaign || undefined,
          utm_content: _utm.utm_content || undefined,
        });
      }
      showToast(
        inquiryMode === 'enterprise'
          ? '문의를 접수했어요. 이메일로 12개월 견적서와 검토 소개서를 보냈어요'
          : '문의를 접수했어요. 이메일로 14일 PRO 쿠폰과 서류를 보냈어요',
        'success',
        6000,
      );
      setShowGroupInquiry(false);
      setInquiryForm({ name: '', email: '', organization: '', phone: '', teacherCount: '', message: '' });
    } catch (error) {
      // 서버가 짧은 한글 안내(detail)를 준 경우에만 덧붙이고, 기술 메시지는 노출하지 않는다.
      const detail = error?.message || '';
      const isFriendlyDetail = detail.length <= 40 && /[가-힣]/.test(detail) && detail !== '문의를 접수하지 못했어요.';
      showToast(
        `문의를 접수하지 못했어요. ${isFriendlyDetail ? detail : '잠시 후 다시 시도해주세요'}`,
        'error',
        5000
      );
    } finally {
      setIsSubmittingInquiry(false);
    }
  };

  const openInquiry = (mode = 'group') => {
    setInquiryMode(mode);
    setShowGroupInquiry(true);
  };

  // 학교 생기부 검토 체험 화면 등에서 /pricing?inquiry=enterprise 로 오면 검토 도입 문의를 바로 연다
  useEffect(() => {
    if (new URLSearchParams(location.search).get('inquiry') !== 'enterprise') return;
    setInquiryMode('enterprise');
    setInquiryForm((prev) => (prev.message ? prev : { ...prev, message: '학교 생기부 검토 도입 문의' }));
    setShowGroupInquiry(true);
  }, [location.search]);

  const handleCompareAction = (planId) => {
    if (planId === 'free') {
      navigate(currentUser ? '/home' : '/auth?tab=signup');
      return;
    }
    if (planId === 'pro') {
      document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (planId === 'pro-school') {
      trackSchoolPurchaseEvent('compare_school_budget_clicked', { entry: 'pricing_compare' });
      navigate(`/bulk-purchase?intent=quote${schoolPromoSlug ? `&promo=${schoolPromoSlug}` : ''}`);
      return;
    }
    navigate(`/bulk-purchase?intent=quote&plan=12month&qty=10${schoolPromoSlug ? `&promo=${schoolPromoSlug}` : ''}`);
  };

  const handlePayment = (plan) => {
    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    // 📊 GA4: 결제 시작 추적
    trackBeginCheckout(plan.id, plan.price);
    if (typeof window.fbq !== 'undefined') {
      const _utm = (() => { try { const s = localStorage.getItem('whatquiz_utm'); if (!s) return {}; const p = JSON.parse(s); return (!p.expiresAt || Date.now() <= p.expiresAt) ? p : {}; } catch { return {}; } })();
      window.fbq('track', 'InitiateCheckout', {
        value: plan.price, currency: 'KRW', content_ids: [plan.id],
        utm_source: _utm.utm_source || undefined, utm_medium: _utm.utm_medium || undefined,
        utm_campaign: _utm.utm_campaign || undefined, utm_content: _utm.utm_content || undefined,
      });
    }
    // 앱/웹 모두 동일하게 결제 페이지로 이동.
    // 모바일 앱이면 결제 페이지(ProSubscription)에서 AppPaymentBlocker가 가드해
    // "웹사이트에서 가입 가능" 안내만 표시. 외부 브라우저 강제 이동을 하지 않아
    // Apple App Store Guideline 3.1.1 (외부 결제 유도 금지)에 안전.
    navigate(`/subscription/pro?plan=${plan.id}&price=${plan.price}${checkoutPromoSlug ? `&promo=${checkoutPromoSlug}` : ''}`);
  };

  const handleClaimCoupon = async () => {
    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    if (!couponCode.trim()) {
      setCouponError('쿠폰 코드를 입력해주세요');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const result = await claimCoupon(couponCode.trim(), currentUser);
      const benefitDays = result.benefit_days || 30;
      // 백엔드가 멱등 응답으로 200 OK를 돌려주는 경우(already_claimed=true)에는
      // 새로 등록된 것이 아니므로 별도 안내 문구로 분기한다.
      if (result.already_claimed) {
        showToast('이미 등록한 쿠폰이에요. PRO 혜택이 적용 중이니 그대로 이용하세요', 'info', 5000);
      } else {
        showToast(`${benefitDays}일 PRO 혜택을 등록했어요`, 'success');
      }
      setCouponCode('');
      await fetchSubscriptionStatus(true);
      navigate('/subscription');
    } catch (error) {
      const msg = error.message || '';
      const isNetwork = msg.includes('네트워크') || msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network');
      if (isNetwork) {
        setCouponError('네트워크 연결을 확인해주세요. 잠시 후 다시 시도해주세요.');
      } else {
        setCouponError(msg || '쿠폰을 등록하지 못했어요. 코드를 다시 확인해주세요.');
      }
    } finally {
      setCouponLoading(false);
    }
  };

  return (
    <div className="pricing-page" ref={pageRef}>
      <Helmet>
        <title>{pro50Active
          ? '요금제 | 왓퀴즈 PRO - 10월 한정 1년 반값 | 학교 예산 결제 가능'
          : '요금제 | 왓퀴즈 PRO - 월 16,667원부터 | 학교 예산 결제 가능'}</title>
        <meta name="description" content={`왓퀴즈 PRO 요금제. 월 16,667원부터 시작. 학교 예산(법인카드, 공용카드) 결제 가능. 세금계산서 발급. ${PRO_MODELS_TEXT} 최신 AI 모델 무제한 사용.`} />
        <meta name="keywords" content="왓퀴즈 요금제, 교사 AI 가격, 생기부 AI 가격, 학교 예산 결제, 세금계산서, GPT 교사, 교육 AI 구독" />
        <link rel="canonical" href="https://whatquiz.co.kr/pricing" />
        <meta property="og:title" content="요금제 | 왓퀴즈 PRO - 월 16,667원부터" />
        <meta property="og:description" content={`학교 예산 결제 가능. ${PRO_MODELS_TEXT} 최신 AI 무제한. 생기부, PPT, 학습지, 이미지, 채점 올인원.`} />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png?v=20260210" />
        <meta property="og:url" content="https://whatquiz.co.kr/pricing" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "왓퀴즈 PRO",
            "description": `교사 전용 AI 서비스. 생기부 자동 작성, 학습지 생성, 수행평가 설계를 AI로 자동화. ${PRO_MODELS_TEXT} 최신 모델 제공.`,
            "brand": {
              "@type": "Brand",
              "name": "왓퀴즈"
            },
            "offers": [
              {
                "@type": "Offer",
                "name": "3개월 플랜",
                "price": "60000",
                "priceCurrency": "KRW",
                "priceValidUntil": "2026-12-31",
                "availability": "https://schema.org/InStock",
                "url": "https://whatquiz.co.kr/pricing"
              },
              {
                "@type": "Offer",
                "name": "6개월 플랜",
                "price": "110000",
                "priceCurrency": "KRW",
                "priceValidUntil": "2026-12-31",
                "availability": "https://schema.org/InStock",
                "url": "https://whatquiz.co.kr/pricing"
              },
              {
                "@type": "Offer",
                "name": "12개월 플랜 (최저가)",
                "price": "200000",
                "priceCurrency": "KRW",
                "priceValidUntil": "2026-12-31",
                "availability": "https://schema.org/InStock",
                "url": "https://whatquiz.co.kr/pricing"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "387",
              "bestRating": "5"
            }
          })}
        </script>
      </Helmet>

      {/* 히어로 섹션 — 학교 예산 안내는 배지 하나로 축약, 상세는 아래 통합 밴드가 담당 */}
      <section className="pricing-hero" data-pr-reveal>
        <div className="pricing-hero-inner">
          {pro50Active ? (
            <Link className="pricing-hero-label" to={buildPro50LandingUrl('pricing_hero')}>
              10월 이벤트 · PRO 1년 반값 · {PRO50_EVENT_END_LABEL}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Link>
          ) : (
          <button
            type="button"
            className="pricing-hero-label"
            onClick={() => {
              document.getElementById('docs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            학교 예산 결제 가능 · 서류 준비 완료
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          )}
          <h1 className="pricing-hero-title">
            생기부부터 채점까지<br />
            <span className="pricing-hero-highlight">왓퀴즈 PRO</span>
          </h1>
          <p className="pricing-hero-desc">
            생기부, 서술형 채점, 한글 문서, 수업 자료를<br />
            최신 AI로 한곳에서 만들어요
          </p>
          <div className="pricing-hero-price">
            <span className="pricing-hero-price-label">월</span>
            <span className="pricing-hero-price-value">{proFromMonthly.toLocaleString()}</span>
            <span className="pricing-hero-price-unit">원부터</span>
          </div>
          <button
            type="button"
            className="pricing-hero-cta"
            onClick={() => {
              document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            요금제 선택하기
          </button>
        </div>
      </section>

      {/* 요금제 카드 */}
      <section className="pricing-plans" id="plans" data-pr-reveal>
        <div className="pricing-plans-header">
          <h2>요금제 선택</h2>
        </div>
        
        <div className="pricing-plans-grid">
          {plans.map((plan) => (
            <div 
              key={plan.id} 
              className={`pricing-plan-card ${plan.badge ? 'popular' : ''}`}
            >
              {plan.badge && <span className="pricing-plan-badge">{plan.badge}</span>}
              <div className="pricing-plan-duration">{plan.duration}</div>
              <div className="pricing-plan-price-wrap">
                <span className="pricing-plan-price">{plan.price.toLocaleString()}원</span>
                <span className="pricing-plan-original">{plan.originalPrice.toLocaleString()}원</span>
              </div>
              <div className="pricing-plan-discount">{plan.discount}% 할인</div>
              <div className="pricing-plan-monthly">월 {plan.monthly.toLocaleString()}원</div>
              <button 
                className="pricing-plan-btn"
                onClick={() => handlePayment(plan)}
              >
                {isPro ? '기간 연장' : plan.ctaText}
              </button>
            </div>
          ))}
        </div>

        {/* 결정 지점 안심 문구 — 상단 공지(자동 결제 없음)를 이곳으로 통합 */}
        <p className="pricing-plans-assurance">
          7일 이내 100% 환불 · 자동 결제 없음 · 세금계산서 발급
        </p>

        {/* 1개월 플랜 */}
        <div className="pricing-short-term">
          <span>잠깐만 써보고 싶다면</span>
          <button onClick={() => handlePayment({ id: '1month', price: oneMonthPrice })}>
            1개월 {oneMonthPrice.toLocaleString()}원
          </button>
        </div>

        <Link className="pricing-training-entry" to="/training">
          <span>연수비·복지포인트로 결제하시나요?</span>
          <strong>연수 + PRO 패키지 보기</strong>
        </Link>

        <Link className="pricing-training-entry" to="/kit">
          <span>12개월 결제 시 학급 키트 선착순 증정</span>
          <strong>학급 키트 보기</strong>
        </Link>

        {/* 쿠폰 등록 — 필요한 사람만 펼치는 컴팩트 인라인 */}
        <details className="pricing-coupon">
          <summary>
            쿠폰 코드가 있으신가요?
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </summary>
          <div className="pricing-coupon-form">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setCouponError('');
              }}
              placeholder="쿠폰 코드 입력"
              disabled={couponLoading}
              onKeyPress={(e) => e.key === 'Enter' && couponCode.trim() && handleClaimCoupon()}
            />
            <button
              onClick={handleClaimCoupon}
              disabled={couponLoading || !couponCode.trim()}
            >
              {couponLoading ? '등록 중...' : '등록'}
            </button>
          </div>
          {couponError && <p className="pricing-coupon-error">{couponError}</p>}
        </details>
      </section>

      {/* 3열 비교표 — 무료 / PRO / 학교(엔터프라이즈) */}
      <section className="pricing-compare" id="compare" data-pr-reveal>
        <h2>한눈에 비교해 보세요</h2>
        <PlanCompareTable
          proPriceLabel={`월 ${proFromMonthly.toLocaleString()}원부터`}
          schoolPriceLabel={schoolPriceLabel}
          onAction={handleCompareAction}
        />
      </section>

      {/* 학교(엔터프라이즈)에 들어 있는 학교 생기부 검토 — 제목 · 한 줄 · 체험 · 소개서 · 도입 문의 */}
      <section className="pricing-review" id="school-review" data-pr-reveal>
        <div className="pricing-review-inner">
          <div className="pricing-review-copy">
            <p className="pricing-review-kicker" data-testid="pricing-review-price">학교(엔터프라이즈)에 포함</p>
            <h2>생기부 파일, 이제 주고받지 마세요</h2>
            <p>선생님은 쓴 글을 직접 올리고, 관리자는 한 화면에서 점검하고 수정 요청해요.</p>
          </div>
          <div className="pricing-review-actions">
            <Link className="pricing-review-primary" to="/enterprise/school-review">가상 학교로 체험해 보기</Link>
            <a className="pricing-review-secondary" href="/docs/whatquiz-school-review.pdf" download>소개서 받기</a>
            <button type="button" className="pricing-review-secondary" onClick={() => openInquiry('enterprise')}>도입 문의하기</button>
          </div>
        </div>
      </section>

      {/* 학교 결제 통합 밴드 — 배너·견적기 임베드·서류 섹션을 하나로 (#docs 앵커 유지) */}
      <section className="pricing-school" id="docs" data-pr-reveal>
        <div className="pricing-school-inner">
          <p className="pricing-school-kicker">학교 · 단체</p>
          <h2>학교 예산으로 결제하세요</h2>
          <p className="pricing-school-desc">
            법인카드·공용카드로 결제할 수 있어요<br />
            {pro50Active
              ? `${PRO50_EVENT_END_LABEL} 인원 제한 없이 1명부터 이벤트가예요`
              : showGroup50
                ? `${GROUP50_EVENT_END_LABEL} 10명 이상이면 1인당 반값이에요`
                : '인원이 많을수록 할인율이 자동으로 올라가요'}
          </p>
          <div className="pricing-school-tiers" aria-label="단체 구매 할인율">
            {pro50Active ? (
              <Link className="pricing-school-tier is-event" to={buildPro50LandingUrl('pricing_band')}>
                10월 이벤트 · 12개월 <b>50%</b>
                <span className="pricing-school-tier-more">자세히 보기</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </Link>
            ) : showGroup50 ? (
              <Link
                className="pricing-school-tier is-event"
                to={buildGroup50LandingUrl('pricing_band')}
                onClick={() => trackGroup50Event('crosslink_clicked', { stage: 'pricing_band' })}
              >
                단체 이벤트 · 10명 이상 <b>50%</b>
                <span className="pricing-school-tier-more">자세히 보기</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </Link>
            ) : (
              groupTiers.map((tier) => (
                <span key={tier.label} className="pricing-school-tier">
                  {tier.label} <b>{Math.round(tier.discountRate * 100)}%</b>
                </span>
              ))
            )}
          </div>
          <div className="pricing-school-trust">
            <a
              href="https://edzip.kr/utilization/learning-sw/697967395c793e86194d3236"
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-school-trust-item"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              에듀집 등록 완료
            </a>
            <span className="pricing-school-trust-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              세금계산서 발급
            </span>
            <span className="pricing-school-trust-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              학운위 심의 대상 아님
            </span>
          </div>
          <div className="pricing-school-actions">
            <button
              type="button"
              className="pricing-school-primary"
              onClick={() => navigate(`/bulk-purchase?intent=quote${schoolPromoSlug ? `&promo=${schoolPromoSlug}` : ''}`)}
            >
              견적서 만들기
              <span>견적서 · 품의서 · 공문 PDF</span>
            </button>
            <a
              href="/docs/whatquiz-all-docs.zip"
              download
              className="pricing-school-secondary"
              onClick={() => trackSchoolPurchaseEvent('docs_zip_clicked', { entry: 'pricing_band' })}
            >
              서류 한 번에 받기
              <span>소개서 · 사용 매뉴얼 · 체크리스트 등 ZIP</span>
            </a>
          </div>
          <p className="pricing-school-note">
            <a
              href="https://s2b.kr/Edutech/product/productDetail.do?estimatecode=202508043796515"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackSchoolPurchaseEvent('s2b_clicked', { entry: 'pricing_band' })}
            >
              S2B 학교장터
            </a>
            에서도 구매할 수 있어요 ·{' '}
            <button type="button" onClick={() => openInquiry('group')}>
              다른 문의하기
            </button>
          </p>
        </div>
      </section>

      {/* FAQ — 아코디언, 첫 항목만 기본 펼침 */}
      <section className="pricing-faq" data-pr-reveal>
        <h2>자주 묻는 질문</h2>
        <div className="pricing-faq-list">
          {faqs.map((faq, idx) => (
            <details key={faq.q} className="pricing-faq-item" open={idx === 0}>
              <summary>
                {faq.q}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </summary>
              <p>{faq.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* 마지막 전환 밴드 — 딥 블루 */}
      <section className="pricing-cta" data-pr-reveal>
        <div className="pricing-cta-inner">
          <h2>더 빠른 퇴근,<br />더 나은 수업</h2>
          <p>7일 이내 100% 환불 보장</p>
          <div className="pricing-cta-buttons">
            <button className="pricing-cta-primary" onClick={() => handlePayment(recommendedPlan)}>
              {recommendedPlan.duration} 시작하기
              <span>월 {recommendedPlan.monthly.toLocaleString()}원</span>
            </button>
            <button className="pricing-cta-secondary" onClick={() => openInquiry('group')}>
              단체 구매 문의
            </button>
          </div>
        </div>
      </section>

      {/* 단체 문의 모달 — Dialog 프리미티브 위의 폼. 접수 중에는 닫히지 않는다 */}
      <Dialog
        closeLabel={`${inquiryTitle} 닫기`}
        description={inquiryMode === 'enterprise'
          ? '학교(엔터프라이즈) 요금제(12개월 · 선생님 10명 이상)에 추가 비용 없이 들어 있어요. 접수하면 견적서와 학교 생기부 검토 소개서를 바로 보내드려요'
          : '접수하면 이메일로 14일 PRO 쿠폰과 도입 서류를 바로 보내드려요'}
        dismissible={!isSubmittingInquiry}
        initialFocus={inquiryNameRef}
        onClose={() => setShowGroupInquiry(false)}
        open={showGroupInquiry}
        panelClassName="pricing-inquiry-dialog"
        size="md"
        testId="pricing-inquiry-dialog"
        title={inquiryTitle}
      >
        <form onSubmit={handleInquirySubmit} className="pricing-modal-form" aria-busy={isSubmittingInquiry || undefined}>
          <div className="pricing-modal-row">
            <div className="pricing-modal-field">
              <label htmlFor="pricing-inquiry-name">담당자명 <span aria-hidden="true">*</span></label>
              <input
                id="pricing-inquiry-name"
                ref={inquiryNameRef}
                type="text"
                autoComplete="name"
                value={inquiryForm.name}
                onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                required
                aria-required="true"
                disabled={isSubmittingInquiry}
                placeholder="홍길동"
              />
            </div>
            <div className="pricing-modal-field">
              <label htmlFor="pricing-inquiry-phone">연락처</label>
              <input
                id="pricing-inquiry-phone"
                type="tel"
                autoComplete="tel"
                value={inquiryForm.phone}
                onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                disabled={isSubmittingInquiry}
                placeholder="010-1234-5678"
              />
            </div>
          </div>
          <div className="pricing-modal-field">
            <label htmlFor="pricing-inquiry-email">이메일 <span aria-hidden="true">*</span></label>
            <input
              id="pricing-inquiry-email"
              type="email"
              autoComplete="email"
              value={inquiryForm.email}
              onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
              required
              aria-required="true"
              disabled={isSubmittingInquiry}
              placeholder="example@school.ac.kr"
            />
          </div>
          <div className="pricing-modal-row">
            <div className="pricing-modal-field">
              <label htmlFor="pricing-inquiry-organization">기관명 <span aria-hidden="true">*</span></label>
              <input
                id="pricing-inquiry-organization"
                type="text"
                autoComplete="organization"
                value={inquiryForm.organization}
                onChange={(e) => setInquiryForm({...inquiryForm, organization: e.target.value})}
                required
                aria-required="true"
                disabled={isSubmittingInquiry}
                placeholder={inquiryMode === 'enterprise' ? 'OO교육청 · OO고등학교' : 'OO고등학교'}
              />
            </div>
            <div className="pricing-modal-field">
              <label htmlFor="pricing-inquiry-teacher-count">예상 인원</label>
              <input
                id="pricing-inquiry-teacher-count"
                type="number"
                min="1"
                inputMode="numeric"
                value={inquiryForm.teacherCount}
                onChange={(e) => setInquiryForm({...inquiryForm, teacherCount: e.target.value})}
                disabled={isSubmittingInquiry}
                placeholder="25"
              />
            </div>
          </div>
          <div className="pricing-modal-field">
            <label htmlFor="pricing-inquiry-message">문의 내용</label>
            <textarea
              id="pricing-inquiry-message"
              value={inquiryForm.message}
              onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
              disabled={isSubmittingInquiry}
              placeholder={inquiryMode === 'enterprise'
                ? '여러 학교 도입, 맞춤 연수처럼 필요한 점을 적어 주세요.'
                : '학교 전체 교사 대상 도입을 검토 중이에요.'}
              rows="3"
            />
          </div>
          <ModalActions
            className="pricing-modal-actions"
            primary={{ label: '문의 보내기', type: 'submit', loading: isSubmittingInquiry }}
            secondary={{ label: '취소', onClick: () => setShowGroupInquiry(false) }}
            testId="pricing-inquiry-actions"
          />
        </form>
      </Dialog>

      <LoginPrompt 
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        feature="default"
      />

      {/* 모바일 스티키 CTA */}
      <div className="pricing-sticky-cta-mobile">
        <button className="pricing-sticky-cta-btn" onClick={() => handlePayment(recommendedPlan)}>
          {recommendedPlan.duration} PRO 시작 · 월 {recommendedPlan.monthly.toLocaleString()}원
        </button>
      </div>

      {/* 푸터 */}
      <Footer />
    </div>
  );
};

export default PricingPage;
